import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { port } from '../../App';
import { 
  createFacebookConfigAPI, 
  getFacebookConfigByIdAPI, 
  updateFacebookConfigAPI 
} from '../../api-wa/facebookApi';
import BackButton from '../../Components/MiniComponent/BackButton';

const FacebookConfigForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');

  const defaultObj = {
    appName: '',
    appId: '',
    appSecret: '',
    verifyToken: '',
    activeStatus: 'active',
    pages: []
  };

  const [formData, setFormData] = useState(defaultObj);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRecruiters();
    if (editId) {
      loadEditData(editId);
    }
  }, [editId]);

  const fetchRecruiters = async () => {
    try {
      const res = await axios.get(`${port}/root/ScreeningAssigning/`);
      setRecruiters(res.data || []);
    } catch (err) {
      console.error('Error fetching recruiters:', err);
    }
  };

  const loadEditData = async (id) => {
    try {
      setLoading(true);
      const res = await getFacebookConfigByIdAPI(id);
      if (res) {
        setFormData({
          appName: res.app_name || '',
          appId: res.app_id || '',
          appSecret: res.app_secret || '',
          verifyToken: res.verify_token || '',
          activeStatus: res.active_status || 'active',
          pages: res.pages?.map(p => ({
            pageName: p.page_name || '',
            pageId: p.page_id || '',
            pageAccessToken: p.page_access_token || '',
            generatedDate: p.generated_date ? new Date(p.generated_date).toISOString().split('T')[0] : '',
            expiryDate: p.expiry_date ? new Date(p.expiry_date).toISOString().split('T')[0] : '',
            activeStatus: p.active_status || 'active',
            allowed_Ad_id: Array.isArray(p.allowed_ad_ids) ? p.allowed_ad_ids.join(', ') : (p.allowed_ad_ids || ''),
            not_allowed_Ad_id: Array.isArray(p.not_allowed_ad_ids) ? p.not_allowed_ad_ids.join(', ') : (p.not_allowed_ad_ids || ''),
            autoAssignEmployee: p.auto_assign_employee || ''
          })) || []
        });
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load configuration details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePageChange = (index, e) => {
    const { name, value } = e.target;
    const newPages = [...formData.pages];
    newPages[index][name] = value;
    setFormData(prev => ({
      ...prev,
      pages: newPages
    }));
  };

  const addPage = () => {
    setFormData(prev => ({
      ...prev,
      pages: [
        ...prev.pages,
        {
          pageName: '',
          pageId: '',
          pageAccessToken: '',
          expiryDate: '',
          generatedDate: '',
          activeStatus: 'active',
          allowed_Ad_id: '',
          not_allowed_Ad_id: '',
          autoAssignEmployee: ''
        }
      ]
    }));
  };

  const removePage = (index) => {
    const newPages = [...formData.pages];
    newPages.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      pages: newPages
    }));
  };

  const validate = () => {
    if (!formData.appName || !formData.appId || !formData.verifyToken || !formData.appSecret) {
      return false;
    }
    for (let page of formData.pages) {
      if (!page.pageName || !page.pageId || !page.pageAccessToken) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.warning('Please fill in all the required fields');
      return;
    }

    try {
      setLoading(true);
      // Map frontend input keys to backend snake_case formats
      const submissionData = {
        _id: editId || undefined,
        appName: formData.appName,
        appId: formData.appId,
        appSecret: formData.appSecret,
        verifyToken: formData.verifyToken,
        activeStatus: formData.activeStatus,
        pages: formData.pages.map(p => ({
          pageName: p.pageName,
          pageId: p.pageId,
          pageAccessToken: p.pageAccessToken,
          generatedDate: p.generatedDate || null,
          expiryDate: p.expiryDate || null,
          activeStatus: p.activeStatus,
          allowed_Ad_id: p.allowed_Ad_id ? p.allowed_Ad_id.split(',').map(id => id.trim()).filter(id => id) : [],
          not_allowed_Ad_id: p.not_allowed_Ad_id ? p.not_allowed_Ad_id.split(',').map(id => id.trim()).filter(id => id) : [],
          autoAssignEmployee: p.autoAssignEmployee || null
        }))
      };

      if (editId) {
        await updateFacebookConfigAPI(submissionData);
        toast.success('Configuration updated successfully');
      } else {
        await createFacebookConfigAPI(submissionData);
        toast.success('Configuration registered successfully');
      }
      navigate('/facebook/configuration');
    } catch (err) {
      console.error(err);
      const errMsg = err?.response?.data || 'Failed to save configuration';
      toast.error(typeof errMsg === 'string' ? errMsg : 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-4 poppins bg-gray-50 min-h-screen">
      <div className="flex items-center gap-4 mb-6">
        <BackButton />
        <h2 className="text-2xl font-bold text-gray-800 m-0">
          {editId ? 'Edit Facebook App' : 'Add New Facebook App'}
        </h2>
      </div>

      {loading && !formData.appName ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* App config card */}
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <h4 className="text-lg font-bold text-gray-800 mb-6 pb-2 border-b">Facebook App Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">App Name *</label>
                <input
                  type="text"
                  name="appName"
                  value={formData.appName}
                  onChange={handleChange}
                  placeholder="e.g. Merida Tech"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">App ID *</label>
                <input
                  type="text"
                  name="appId"
                  value={formData.appId}
                  onChange={handleChange}
                  placeholder="e.g. 1687219435832367"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">App Secret *</label>
                <input
                  type="text"
                  name="appSecret"
                  value={formData.appSecret}
                  onChange={handleChange}
                  placeholder="Developer app secret key"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Verify Token *</label>
                <input
                  type="text"
                  name="verifyToken"
                  value={formData.verifyToken}
                  onChange={handleChange}
                  placeholder="e.g. Meridahrsite@123"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Active Status</label>
                <select
                  name="activeStatus"
                  value={formData.activeStatus}
                  onChange={handleChange}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white outline-none transition-all focus:border-indigo-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Connected pages section */}
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <div className="flex justify-between items-center mb-6 pb-2 border-b">
              <h4 className="text-lg font-bold text-gray-800 m-0">Connected Pages</h4>
              <button
                type="button"
                onClick={addPage}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1.5 rounded-xl text-xs shadow-sm transition-all"
              >
                Add Page
              </button>
            </div>

            <div className="space-y-6">
              {formData.pages.map((page, index) => (
                <div key={index} className="bg-slate-50 border rounded-2xl p-6 relative">
                  <button
                    type="button"
                    onClick={() => removePage(index)}
                    className="absolute top-4 right-4 text-red-500 hover:text-red-700 font-bold text-xs flex items-center gap-1 border border-red-200 hover:border-red-300 bg-white px-2 py-1 rounded-xl transition-all"
                  >
                    Remove
                  </button>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Page Name *</label>
                      <input
                        type="text"
                        name="pageName"
                        value={page.pageName}
                        onChange={(e) => handlePageChange(index, e)}
                        placeholder="e.g. Merida Tech Minds"
                        className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Page ID *</label>
                      <input
                        type="text"
                        name="pageId"
                        value={page.pageId}
                        onChange={(e) => handlePageChange(index, e)}
                        placeholder="Facebook Page ID"
                        className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                        required
                      />
                    </div>

                    <div className="col-span-1 md:col-span-2">
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Page Access Token *</label>
                      <textarea
                        name="pageAccessToken"
                        value={page.pageAccessToken}
                        onChange={(e) => handlePageChange(index, e)}
                        placeholder="Facebook Graph Page Access Token"
                        rows={2}
                        className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500 font-mono"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Generated Date</label>
                      <input
                        type="date"
                        name="generatedDate"
                        value={page.generatedDate}
                        onChange={(e) => handlePageChange(index, e)}
                        className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Expiry Date</label>
                      <input
                        type="date"
                        name="expiryDate"
                        value={page.expiryDate}
                        onChange={(e) => handlePageChange(index, e)}
                        className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Active Status</label>
                      <select
                        name="activeStatus"
                        value={page.activeStatus}
                        onChange={(e) => handlePageChange(index, e)}
                        className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Auto Assign Employee</label>
                      <select
                        name="autoAssignEmployee"
                        value={page.autoAssignEmployee}
                        onChange={(e) => handlePageChange(index, e)}
                        className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                      >
                        <option value="">Select Employee...</option>
                        {recruiters.map((rec) => (
                          <option key={rec.id} value={rec.EmployeeId}>
                            {rec.Name} ({rec.EmployeeId})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Allowed Ad IDs (comma separated)</label>
                      <input
                        type="text"
                        name="allowed_Ad_id"
                        value={page.allowed_Ad_id}
                        onChange={(e) => handlePageChange(index, e)}
                        placeholder="e.g. ad123, ad456"
                        className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 block mb-1">Not Allowed Ad IDs (comma separated)</label>
                      <input
                        type="text"
                        name="not_allowed_Ad_id"
                        value={page.not_allowed_Ad_id}
                        onChange={(e) => handlePageChange(index, e)}
                        placeholder="e.g. ad789, ad012"
                        className="w-full border border-gray-200 bg-white rounded-xl px-3 py-2 text-sm outline-none transition-all focus:border-indigo-500"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {formData.pages.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-xs font-medium border border-dashed rounded-2xl">
                  No pages added. Click "Add Page" to connect a page token.
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/facebook/configuration')}
              className="border border-gray-200 hover:bg-gray-100 text-gray-700 font-semibold px-6 py-2 rounded-xl text-sm transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-2 rounded-xl text-sm shadow-sm transition-all disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Submit'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default FacebookConfigForm;
